import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from '@/components/formatted_message/FormattedMessage';
import type { MergedSupplyPointDetail, SupplyPointMeasurementLocation } from '@enmon/commons/lib/enmon';
import {
  GasCapacityType,
  GasPressureLevel,
  MeasurementLocation,
  SupplyPointLossCalculationMethod,
  SupplyPointTransformerOwnership,
  ThermalTariffType,
  ThermalTransferStationType,
} from '@enmon/commons/lib/enmon';
import { APP_DICTIONARY } from '../root';

export const SUPPLY_POINT_DETAIL_TRANSLATED = defineMessages({
  summaryElectricitySupplyPoint: { id: 'app.common.supplyPoint.detail.summaryElectricitySupplyPoint' },
  summaryGasSupplyPoint: { id: 'app.common.supplyPoint.detail.summaryGasSupplyPoint' },
  generatorType: { id: 'app.common.supplyPoint.detail.generatorType' },
  installedElectricalCapacity: { id: 'app.common.supplyPoint.detail.installedElectricalCapacity' },
  installedHeatCapacity: { id: 'app.common.supplyPoint.detail.installedHeatCapacity' },
  nominalVoltage: { id: 'app.common.supplyPoint.detail.nominalVoltage' },
  electricityMeasurementCategory: { id: 'app.common.supplyPoint.detail.measurementCategory' },
  hasSmartMeter: { id: 'app.common.supplyPoint.detail.hasSmartMeter' },
  powerFactor: { id: 'app.common.supplyPoint.detail.powerFactor' },
  distributionRate: APP_DICTIONARY.distributionRate,
  circuitBreaker: { id: 'app.common.supplyPoint.detail.circuitBreaker' },
  numberOfPhases: { id: 'app.common.supplyPoint.detail.numberOfPhases' },
  reservedCapacityHighVoltageLevels: { id: 'app.common.supplyPoint.detail.reservedCapacityHighVoltageLevels' },
  monthlyReservedCapacity: { id: 'app.common.supplyPoint.detail.monthlyReservedCapacity' },
  quarterlyReservedCapacity: { id: 'app.common.supplyPoint.detail.quarterlyReservedCapacity' },
  annualReservedCapacity: { id: 'app.common.supplyPoint.detail.annualReservedCapacity' },
  maxReservedPower: { id: 'app.common.supplyPoint.detail.maxReservedPower' },
  overflowLimit: { id: 'app.common.supplyPoint.detail.overflowLimit' },
  measurementLocation: { id: 'app.common.supplyPoint.detail.measurementLocation' },
  transformerOwnership: { id: 'app.common.supplyPoint.detail.transformerOwnership' },
  lossCalculationMethod: { id: 'app.common.supplyPoint.detail.lossCalculationMethod' },
  transformerRatedPower: { id: 'app.common.supplyPoint.detail.transformerRatedPower' },
  noLoadLosses: { id: 'app.common.supplyPoint.detail.noLoadLosses' },
  shortCircuitLosses: { id: 'app.common.supplyPoint.detail.shortCircuitLosses' },
  maxInput: { id: 'app.common.supplyPoint.detail.maxInput' },
  agreedInput: { id: 'app.common.supplyPoint.detail.agreedInput' },
  peakReservedInput: { id: 'app.common.supplyPoint.detail.peakReservedInput' },
  heatProductionEfficiency: { id: 'app.common.supplyPoint.detail.heatProductionEfficiency' },
  electricityProductionEfficiency: { id: 'app.common.supplyPoint.detail.electricityProductionEfficiency' },
  oteDistributionPricelist: { id: 'app.common.supplyPoint.detail.oteDistributionPricelist' },
  supplierPricelist: { id: 'app.common.supplyPoint.detail.supplierPricelist' },
  agreedThermalPower: {
    id: 'app.common.supplyPoint.detail.agreedThermalPower',
  },
  annualAgreedAmount: {
    id: 'app.common.supplyPoint.detail.annualAgreedAmount',
  },
  annualDrinkingWaterVolume: {
    id: 'app.common.supplyPoint.detail.annualDrinkingWaterVolume',
  },
  annualPrecipitation: {
    id: 'app.common.supplyPoint.detail.annualPrecipitation',
  },
  annualWasteWaterVolume: {
    id: 'app.common.supplyPoint.detail.annualWasteWaterVolume',
  },
  billingUnit: { id: 'app.common.supplyPoint.detail.billingUnit' },
  builtUpArea: { id: 'app.common.supplyPoint.detail.builtUpArea' },
  calorificValue: { id: 'app.common.supplyPoint.detail.calorificValue' },
  capacityType: { id: 'app.common.supplyPoint.detail.capacityType' },
  categorySpecification: {
    id: 'app.common.supplyPoint.detail.categorySpecification',
  },
  connectionCapacity: {
    id: 'app.common.supplyPoint.detail.connectionCapacity',
  },
  coolingTemperature: {
    id: 'app.common.supplyPoint.detail.coolingTemperature',
  },
  dailyReservedCapacity: {
    id: 'app.common.supplyPoint.detail.dailyReservedCapacity',
  },
  emissionFactor: { id: 'app.common.supplyPoint.detail.emissionFactor' },
  gasMeasurementCategory: {
    id: 'app.common.supplyPoint.detail.gasMeasurementCategory',
  },
  hasOwnWaterSource: {
    id: 'app.common.supplyPoint.detail.hasOwnWaterSource',
  },
  heatingTemperature: {
    id: 'app.common.supplyPoint.detail.heatingTemperature',
  },
  pavedArea: { id: 'app.common.supplyPoint.detail.pavedArea' },
  pressureLevel: { id: 'app.common.supplyPoint.detail.pressureLevel' },
  reducedArea: { id: 'app.common.supplyPoint.detail.reducedArea' },
  tariffType: { id: 'app.common.supplyPoint.detail.tariffType' },
  transferStationType: {
    id: 'app.common.supplyPoint.detail.transferStationType',
  },
  unpavedArea: { id: 'app.common.supplyPoint.detail.unpavedArea' },
  waterNotDischargedToSewer: {
    id: 'app.common.supplyPoint.detail.waterNotDischargedToSewer',
  },
  agreedHeatAmount: {
    id: 'app.common.supplyPoint.detail.agreedHeatAmount',
  },
  agreedColdAmount: {
    id: 'app.common.supplyPoint.detail.agreedColdAmount',
  },
} satisfies Record<keyof MergedSupplyPointDetail, MessageDescriptor>);

/** Shown on calculation-critical fields that are locked while editing a revision (they need a new revision). */
export const SUPPLY_POINT_CALC_CRITICAL_FIELD_TRANSLATED = defineMessages({
  help: { id: 'app.common.supplyPoint.detail.calcCriticalFieldHelp' },
});

export const SUPPLY_POINT_DETAIL_TOOLTIP_TRANSLATED = defineMessages({
  summaryElectricitySupplyPoint: { id: 'app.common.supplyPoint.detail.summaryElectricitySupplyPoint.tooltip' },
  summaryGasSupplyPoint: { id: 'app.common.supplyPoint.detail.summaryGasSupplyPoint.tooltip' },
  distributionRate: { id: 'app.common.supplyPoint.detail.distributionRate.tooltip' },
  circuitBreaker: { id: 'app.common.supplyPoint.detail.circuitBreaker.tooltip' },
  numberOfPhases: { id: 'app.common.supplyPoint.detail.numberOfPhases.tooltip' },
  nominalVoltage: { id: 'app.common.supplyPoint.detail.nominalVoltage.tooltip' },
  powerFactor: { id: 'app.common.supplyPoint.detail.powerFactor.tooltip' },
  maxReservedPower: { id: 'app.common.supplyPoint.detail.maxReservedPower.tooltip' },
  overflowLimit: { id: 'app.common.supplyPoint.detail.overflowLimit.tooltip' },
  electricityMeasurementCategory: { id: 'app.common.supplyPoint.detail.measurementCategory.tooltip' },
  hasSmartMeter: { id: 'app.common.supplyPoint.detail.hasSmartMeter.tooltip' },
  measurementLocation: { id: 'app.common.supplyPoint.detail.measurementLocation.tooltip' },
  transformerOwnership: { id: 'app.common.supplyPoint.detail.transformerOwnership.tooltip' },
  lossCalculationMethod: { id: 'app.common.supplyPoint.detail.lossCalculationMethod.tooltip' },
  transformerRatedPower: { id: 'app.common.supplyPoint.detail.transformerRatedPower.tooltip' },
  noLoadLosses: { id: 'app.common.supplyPoint.detail.noLoadLosses.tooltip' },
  shortCircuitLosses: { id: 'app.common.supplyPoint.detail.shortCircuitLosses.tooltip' },
  maxInput: { id: 'app.common.supplyPoint.detail.maxInput.tooltip' },
  agreedInput: { id: 'app.common.supplyPoint.detail.agreedInput.tooltip' },
  peakReservedInput: { id: 'app.common.supplyPoint.detail.peakReservedInput.tooltip' },
  pressureLevel: { id: 'app.common.supplyPoint.detail.pressureLevel.tooltip' },
  connectionCapacity: { id: 'app.common.supplyPoint.detail.connectionCapacity.tooltip' },
  gasMeasurementCategory: { id: 'app.common.supplyPoint.detail.gasMeasurementCategory.tooltip' },
  dailyReservedCapacity: { id: 'app.common.supplyPoint.detail.dailyReservedCapacity.tooltip' },
  annualAgreedAmount: { id: 'app.common.supplyPoint.detail.annualAgreedAmount.tooltip' },
  capacityType: { id: 'app.common.supplyPoint.detail.capacityType.tooltip' },
  transferStationType: { id: 'app.common.supplyPoint.detail.transferStationType.tooltip' },
  agreedThermalPower: { id: 'app.common.supplyPoint.detail.agreedThermalPower.tooltip' },
  tariffType: { id: 'app.common.supplyPoint.detail.tariffType.tooltip' },
  heatingTemperature: { id: 'app.common.supplyPoint.detail.heatingTemperature.tooltip' },
  coolingTemperature: { id: 'app.common.supplyPoint.detail.coolingTemperature.tooltip' },
  agreedHeatAmount: { id: 'app.common.supplyPoint.detail.agreedHeatAmount.tooltip' },
  agreedColdAmount: { id: 'app.common.supplyPoint.detail.agreedColdAmount.tooltip' },
  builtUpArea: { id: 'app.common.supplyPoint.detail.builtUpArea.tooltip' },
  pavedArea: { id: 'app.common.supplyPoint.detail.pavedArea.tooltip' },
  unpavedArea: { id: 'app.common.supplyPoint.detail.unpavedArea.tooltip' },
  reducedArea: { id: 'app.common.supplyPoint.detail.reducedArea.tooltip' },
  annualPrecipitation: { id: 'app.common.supplyPoint.detail.annualPrecipitation.tooltip' },
  annualDrinkingWaterVolume: { id: 'app.common.supplyPoint.detail.annualDrinkingWaterVolume.tooltip' },
  annualWasteWaterVolume: { id: 'app.common.supplyPoint.detail.annualWasteWaterVolume.tooltip' },
  waterNotDischargedToSewer: { id: 'app.common.supplyPoint.detail.waterNotDischargedToSewer.tooltip' },
  hasOwnWaterSource: { id: 'app.common.supplyPoint.detail.hasOwnWaterSource.tooltip' },
  billingUnit: { id: 'app.common.supplyPoint.detail.billingUnit.tooltip' },
  calorificValue: { id: 'app.common.supplyPoint.detail.calorificValue.tooltip' },
  emissionFactor: { id: 'app.common.supplyPoint.detail.emissionFactor.tooltip' },
  categorySpecification: { id: 'app.common.supplyPoint.detail.categorySpecification.tooltip' },
  generatorType: { id: 'app.common.supplyPoint.detail.generatorType.tooltip' },
  installedElectricalCapacity: { id: 'app.common.supplyPoint.detail.installedElectricalCapacity.tooltip' },
  installedHeatCapacity: { id: 'app.common.supplyPoint.detail.installedHeatCapacity.tooltip' },
  electricityProductionEfficiency: { id: 'app.common.supplyPoint.detail.electricityProductionEfficiency.tooltip' },
  heatProductionEfficiency: { id: 'app.common.supplyPoint.detail.heatProductionEfficiency.tooltip' },
});

export const SUPPLY_POINT_DETAIL_SPECIFIC_FIELDS = defineMessages({
  voltageLevel: { id: 'app.common.supplyPoint.specificField.voltageLevel' },
  nominalVoltage: { id: 'app.common.supplyPoint.specificField.nominalVoltage' },
  powerFactor: { id: 'app.common.supplyPoint.specificField.powerFactor' },
  monthlyReservedCapacity: { id: 'app.common.supplyPoint.specificField.monthlyReservedCapacity' },
  quarterlyReservedCapacity: { id: 'app.common.supplyPoint.specificField.quarterlyReservedCapacity' },
  annualReservedCapacity: { id: 'app.common.supplyPoint.specificField.annualReservedCapacity' },
  maxReservedPower: { id: 'app.common.supplyPoint.specificField.maxReservedPower' },
  overflowLimit: { id: 'app.common.supplyPoint.specificField.overflowLimit' },
  electricityMeasurementCategory: { id: 'app.common.supplyPoint.specificField.measurementType' },
  hasSmartMeter: { id: 'app.common.supplyPoint.specificField.hasSmartMeter' },
  measurementLocation: { id: 'app.common.supplyPoint.specificField.measurementLocation' },
  transformerOwnership: { id: 'app.common.supplyPoint.specificField.transformerOwnership' },
  lossCalculationMethod: { id: 'app.common.supplyPoint.specificField.lossCalculationMethod' },
  transformerRatedPower: { id: 'app.common.supplyPoint.specificField.transformerRatedPower' },
  noLoadLosses: { id: 'app.common.supplyPoint.specificField.noLoadLosses' },
  shortCircuitLosses: { id: 'app.common.supplyPoint.specificField.shortCircuitLosses' },
  maxInput: { id: 'app.common.supplyPoint.specificField.maxInput' },
  agreedInput: { id: 'app.common.supplyPoint.specificField.agreedInput' },
  peakReservedInput: { id: 'app.common.supplyPoint.specificField.peakReservedInput' },
});

export const SUPPLY_POINT_MEASUREMENT_LOCATION_TRANSLATED = defineMessages<SupplyPointMeasurementLocation>({
  [MeasurementLocation.LowVoltage]: { id: 'app.common.supplyPoint.detail.measurementLocation.lowVoltage' },
  [MeasurementLocation.HighVoltage]: { id: 'app.common.supplyPoint.detail.measurementLocation.highVoltage' },
});

export const SUPPLY_POINT_TRANSFORMER_OWNERSHIP_TRANSLATED = defineMessages<SupplyPointTransformerOwnership>({
  [SupplyPointTransformerOwnership.Own]: { id: 'app.common.supplyPoint.detail.transformerOwnership.own' },
  [SupplyPointTransformerOwnership.Distributor]: {
    id: 'app.common.supplyPoint.detail.transformerOwnership.distributor',
  },
});

export const SUPPLY_POINT_LOSS_CALCULATION_METHOD_TRANSLATED = defineMessages<SupplyPointLossCalculationMethod>({
  [SupplyPointLossCalculationMethod.Exact]: { id: 'app.common.supplyPoint.detail.lossCalculationMethod.exact' },
  [SupplyPointLossCalculationMethod.LumpSum]: { id: 'app.common.supplyPoint.detail.lossCalculationMethod.lumpSum' },
});

export const SUPPLY_POINT_GAS_PRESSURE_LEVEL_TRANSLATED = defineMessages<GasPressureLevel>({
  [GasPressureLevel.LowPressure]: { id: 'app.common.supplyPoint.gasPressureLevel.LowPressure' },
  [GasPressureLevel.MediumPressure]: { id: 'app.common.supplyPoint.gasPressureLevel.MediumPressure' },
  [GasPressureLevel.HighPressure]: { id: 'app.common.supplyPoint.gasPressureLevel.HighPressure' },
});

export const SUPPLY_POINT_GAS_CAPACITY_TYPE_TRANSLATED = defineMessages<GasCapacityType>({
  [GasCapacityType.Fixed]: { id: 'app.common.supplyPoint.gasCapacityType.Fixed' },
  [GasCapacityType.Interruptible]: { id: 'app.common.supplyPoint.gasCapacityType.Interruptible' },
});

export const SUPPLY_POINT_THERMAL_TRANSFER_STATION_TYPE_TRANSLATED = defineMessages<ThermalTransferStationType>({
  [ThermalTransferStationType.SteamWaterExchanger]: {
    id: 'app.common.supplyPoint.thermalTransferStationType.SteamWaterExchanger',
  },
  [ThermalTransferStationType.WaterWaterExchanger]: {
    id: 'app.common.supplyPoint.thermalTransferStationType.WaterWaterExchanger',
  },
  [ThermalTransferStationType.CompactSubstation]: {
    id: 'app.common.supplyPoint.thermalTransferStationType.CompactSubstation',
  },
  [ThermalTransferStationType.Independent]: {
    id: 'app.common.supplyPoint.thermalTransferStationType.Independent',
  },
});

export const SUPPLY_POINT_THERMAL_TARIFF_TYPE_TRANSLATED = defineMessages<ThermalTariffType>({
  [ThermalTariffType.SinglePart]: { id: 'app.common.supplyPoint.thermalTariffType.SinglePart' },
  [ThermalTariffType.TwoPart]: { id: 'app.common.supplyPoint.thermalTariffType.TwoPart' },
});
